import React, { useState, useEffect } from "react";
import {
  Scale, Brain, Flame, Layers, Compass, TrendingUp, Shield, Network, Target,
  Circle, AlertTriangle, CheckCircle2, BookOpen, Loader2, ChevronDown, ChevronUp
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const ICON_MAP = {
  scale: Scale, brain: Brain, flame: Flame, layers: Layers,
  compass: Compass, "trending-up": TrendingUp, shield: Shield,
  network: Network, target: Target,
};

const RISK_STYLES = {
  high: { bg: "bg-red-500/10", border: "border-red-500/40", text: "text-red-400", icon: AlertTriangle },
  targeted: { bg: "bg-amber-500/10", border: "border-amber-500/40", text: "text-amber-400", icon: AlertTriangle },
  stable: { bg: "bg-emerald-500/10", border: "border-emerald-500/40", text: "text-emerald-400", icon: CheckCircle2 },
};

const LEVEL_STYLES = {
  High: { bar: "bg-emerald-500", text: "text-emerald-400" },
  Medium: { bar: "bg-amber-500", text: "text-amber-400" },
  Low: { bar: "bg-red-500", text: "text-red-400" },
  "N/A": { bar: "bg-gray-500", text: "text-gray-400" },
};

export default function DiagnosticResultsView({ token, clientId, clientName }) {
  const [data, setData] = useState(null);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedModule, setExpandedModule] = useState(null);

  const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [modRes, resRes] = await Promise.all([
          fetch(`${API}/api/life-diagnostic/modules`, { headers }),
          fetch(`${API}/api/life-diagnostic/results/${clientId}`, { headers }),
        ]);
        const modData = await modRes.json();
        const resData = await resRes.json();
        setModules(modData.modules || []);
        setData(resData);
      } catch (e) { console.error(e); }
      setLoading(false);
    })();
  }, [clientId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-5 h-5 text-divine-gold animate-spin" />
        <span className="ml-2 text-chakra-crown/50 text-sm">Loading diagnostic...</span>
      </div>
    );
  }

  if (!data || !data.modules_completed?.length) {
    return (
      <div className="text-center py-6">
        <Target className="w-8 h-8 text-divine-gold/20 mx-auto mb-2" />
        <p className="text-chakra-crown/50 text-sm">Client has not started the Life Diagnostic yet</p>
      </div>
    );
  }

  const { scores, risk, recommendations, module_recommendations, modules_completed } = data;

  return (
    <div className="space-y-4" data-testid="diagnostic-results-view">
      {/* Progress */}
      <div className="flex items-center justify-between">
        <h4 className="font-heading text-sm text-divine-gold">LIFE DIAGNOSTIC ({modules_completed.length}/9 modules)</h4>
        {data.completed && <span className="text-xs px-2 py-1 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">Complete</span>}
      </div>

      {/* Risk Banner */}
      {risk && (
        <div className={`p-3 rounded-xl border ${RISK_STYLES[risk.risk]?.bg} ${RISK_STYLES[risk.risk]?.border}`}>
          <div className="flex items-center gap-2">
            {React.createElement(RISK_STYLES[risk.risk]?.icon || AlertTriangle, { className: `w-4 h-4 ${RISK_STYLES[risk.risk]?.text}` })}
            <p className={`text-sm ${RISK_STYLES[risk.risk]?.text}`}>{risk.label}</p>
          </div>
        </div>
      )}

      {/* Score Grid */}
      <div className="grid grid-cols-2 gap-2">
        {modules.filter(m => m.id <= 8).map(m => {
          const s = scores[String(m.id)];
          const style = LEVEL_STYLES[s?.level] || LEVEL_STYLES["N/A"];
          const Icon = ICON_MAP[m.icon] || Circle;
          const isCompleted = modules_completed.includes(m.id);
          const isExpanded = expandedModule === m.id;

          return (
            <div key={m.id}
              className={`p-3 rounded-lg border cursor-pointer transition-all ${
                isCompleted ? "bg-cosmic-light/5 border-divine-gold/10" : "bg-cosmic-deep/30 border-divine-gold/5 opacity-50"
              }`}
              onClick={() => setExpandedModule(isExpanded ? null : m.id)}
            >
              <div className="flex items-center gap-2 mb-2">
                <Icon className={`w-4 h-4 ${style.text}`} />
                <span className="text-xs font-heading text-divine-gold truncate flex-1">{m.name}</span>
                <span className={`text-sm font-heading ${style.text}`}>{s?.average?.toFixed(1) || "-"}</span>
              </div>
              <div className="h-1.5 bg-cosmic-deep rounded-full overflow-hidden">
                <div className={`h-full rounded-full ${style.bar}`} style={{ width: `${((s?.average || 0) / 5) * 100}%` }} />
              </div>
              <div className="flex justify-between mt-1">
                <span className={`text-[10px] ${style.text}`}>{s?.level || "N/A"}</span>
                {isExpanded ? <ChevronUp className="w-3 h-3 text-chakra-crown/30" /> : <ChevronDown className="w-3 h-3 text-chakra-crown/30" />}
              </div>
              {isExpanded && module_recommendations[String(m.id)] && (
                <div className="mt-2 pt-2 border-t border-divine-gold/10 space-y-1.5">
                  {module_recommendations[String(m.id)].practices.map((p, i) => (
                    <div key={i} className="flex items-start gap-1.5">
                      <BookOpen className="w-3 h-3 text-divine-gold mt-0.5 shrink-0" />
                      <p className="text-[10px] text-chakra-crown/60 leading-relaxed">{p.title}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Computed Recommendations */}
      {recommendations?.length > 0 && (
        <div>
          <h4 className="font-heading text-xs text-divine-gold mb-2">COACHING GUIDANCE</h4>
          <div className="space-y-1.5">
            {recommendations.map((r, i) => {
              const mod = modules.find(m => m.id === r.module);
              return (
                <div key={i} className="p-2 rounded-lg bg-cosmic-light/5 border border-divine-gold/5">
                  <span className="text-divine-gold text-[10px] font-heading">{mod?.name}</span>
                  <p className="text-chakra-crown/60 text-xs">{r.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Per-Module Practice Recommendations (full report) */}
      {Object.keys(module_recommendations || {}).length > 0 && (
        <div>
          <h4 className="font-heading text-xs text-divine-gold mb-2">PRACTICE RECOMMENDATIONS</h4>
          <div className="space-y-3">
            {Object.entries(module_recommendations).map(([mid, recData]) => (
              <div key={mid} className="p-3 rounded-lg bg-cosmic-light/5 border border-divine-gold/10">
                <h5 className="font-heading text-xs text-divine-gold mb-2">{recData.title}</h5>
                <div className="space-y-2">
                  {recData.practices.map((p, i) => (
                    <div key={i} className="p-2 rounded-lg bg-cosmic-deep/50 border border-divine-gold/5">
                      <div className="flex items-start gap-2">
                        <BookOpen className="w-3.5 h-3.5 text-divine-gold mt-0.5 shrink-0" />
                        <div>
                          <p className="text-divine-gold text-xs font-medium">{p.title}</p>
                          <p className="text-chakra-crown/60 text-[11px] mt-1 leading-relaxed">{p.description}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Overall Results (only when all 9 completed) */}
      {data.completed && recommendations?.length === 0 && (
        <div className="text-center py-4">
          <CheckCircle2 className="w-6 h-6 text-emerald-400 mx-auto mb-1" />
          <p className="text-emerald-400 text-xs font-heading">All sections in healthy range</p>
        </div>
      )}
    </div>
  );
}
